// Tandem Bubble UI System — speaker-aware bubble presentation on top of BubbleUiSystem.
//
// Tandem renders a **merged transcript** of two underlying Chat instances (instructor + engineer)
// plus the real user. The underlying `role` of each bubble stays "user" / "assistant" so that
// bubble chrome, checkpoint restore, and inferenceChatBinder all keep working unchanged.
// On top of that we stamp a `tandemSpeaker` (and `tandemLane`) per bubble so CSS + future tool
// code can tell who is talking without mutating controllers or the binder.

import { BubbleUiSystem } from "../../../ui/index.js";

/** Stable speaker ids used across tandem (CSS classes and attributes mirror these strings). */
export const TANDEM_SPEAKERS = Object.freeze({
  USER: "user",
  INSTRUCTOR: "instructor",
  ENGINEER: "engineer",
  SYSTEM: "system",
});

/** Tandem "lane" = which internal Chat a message belongs to (not necessarily the speaker). */
export const TANDEM_LANES = Object.freeze({
  INSTRUCTOR: "instructor",
  ENGINEER: "engineer",
});

const DEFAULT_DISPLAY_NAMES = Object.freeze({
  [TANDEM_SPEAKERS.USER]: "User",
  [TANDEM_SPEAKERS.INSTRUCTOR]: "Instructor",
  [TANDEM_SPEAKERS.ENGINEER]: "Engineer",
  [TANDEM_SPEAKERS.SYSTEM]: "System",
});

const USER_PRESETS = new Set(["userText", "userImages", "userFiles"]);

function inferRoleFromPreset(preset, props = {}) {
  if (typeof preset === "string") {
    return USER_PRESETS.has(preset) ? "user" : "assistant";
  }
  return props?.role || preset?.role || "assistant";
}

export class TandemBubbleUiSystem extends BubbleUiSystem {
  constructor(options = {}) {
    super(options);

    /**
     * Which assistant speaker should own new assistant bubbles created from now on
     * (instructor or engineer). `TandemManager.setActiveLane(...)` updates this before
     * each `infer()` so the merged transcript stays labeled correctly.
     * @type {"instructor" | "engineer"}
     */
    this.activeAssistantSpeaker = TANDEM_SPEAKERS.ENGINEER;

    /**
     * Which lane new user messages are being authored into. In a standard turn
     * this is the chat that is about to receive inference — the engineer chat
     * sees the instructor as "user", the instructor chat sees the real user.
     * @type {"instructor" | "engineer"}
     */
    this.activeLane = TANDEM_LANES.ENGINEER;

    /**
     * Override: user bubbles created while this is set will be stamped with this
     * speaker instead of "user" (used when the instructor's message is added to
     * the engineer's chat as a user segment — the bubble in the merged view
     * should still read "Instructor").
     * @type {string | null}
     */
    this.pendingUserSpeakerOverride = null;

    /** @type {Record<string, string>} */
    this.displayNames = { ...DEFAULT_DISPLAY_NAMES };
  }

  /**
   * @param {"instructor" | "engineer"} speaker
   */
  setActiveAssistantSpeaker(speaker) {
    if (speaker !== TANDEM_SPEAKERS.INSTRUCTOR && speaker !== TANDEM_SPEAKERS.ENGINEER) {
      return;
    }
    this.activeAssistantSpeaker = speaker;
  }

  /**
   * @param {"instructor" | "engineer"} lane
   */
  setActiveLane(lane) {
    if (lane !== TANDEM_LANES.INSTRUCTOR && lane !== TANDEM_LANES.ENGINEER) {
      return;
    }
    this.activeLane = lane;
  }

  /**
   * Set a one-shot override for the *next* user bubble(s) so they render as a
   * non-real-user speaker (e.g. instructor voice in the engineer chat). The
   * manager is expected to clear this by passing `null` after the message is added.
   * @param {string | null} speaker
   */
  setPendingUserSpeakerOverride(speaker) {
    this.pendingUserSpeakerOverride = speaker || null;
  }

  /**
   * Override a display name (e.g. use the real username instead of "User").
   * @param {string} speaker
   * @param {string} displayName
   */
  setDisplayName(speaker, displayName) {
    if (!speaker || typeof displayName !== "string") return;
    this.displayNames[speaker] = displayName;
  }

  _resolveSpeakerFor(role) {
    if (role === "user") {
      return this.pendingUserSpeakerOverride || TANDEM_SPEAKERS.USER;
    }
    return this.activeAssistantSpeaker || TANDEM_SPEAKERS.ENGINEER;
  }

  _displayNameFor(speaker) {
    return this.displayNames[speaker] || DEFAULT_DISPLAY_NAMES[speaker] || speaker || "";
  }

  /**
   * Inject tandem speaker metadata into the bubble config, then delegate to the
   * base `createBubble`. Honors explicit `tandemSpeaker` / `tandemDisplayName`
   * on the caller's side so tools and the binder can override if they wish.
   */
  createBubble(preset, props = {}) {
    const role = inferRoleFromPreset(preset, props);
    const explicitSpeaker =
      props.tandemSpeaker || (typeof preset === "object" && preset?.tandemSpeaker) || null;
    const tandemSpeaker = explicitSpeaker || this._resolveSpeakerFor(role);
    const tandemLane =
      props.tandemLane ||
      (typeof preset === "object" && preset?.tandemLane) ||
      this.activeLane;
    const tandemDisplayName =
      props.tandemDisplayName ||
      (typeof preset === "object" && preset?.tandemDisplayName) ||
      this._displayNameFor(tandemSpeaker);

    if (typeof preset === "string") {
      return super.createBubble(preset, {
        ...props,
        tandemSpeaker,
        tandemLane,
        tandemDisplayName,
      });
    }

    return super.createBubble(
      {
        ...preset,
        tandemSpeaker,
        tandemLane,
        tandemDisplayName,
      },
      props
    );
  }

  /**
   * Extend base chrome with tandem speaker/lane classes and a label data attribute
   * used by CSS `::before` to render the speaker chip. Attributes on the row
   * element survive bubble HTML streaming (innerHTML replaces children only).
   */
  _applyBubbleChrome(bubble) {
    super._applyBubbleChrome(bubble);

    const rootElement = bubble?.rootElement;
    if (!rootElement) {
      return;
    }

    const speaker = bubble.tandemSpeaker || null;
    const lane = bubble.tandemLane || null;
    const label = bubble.tandemDisplayName || this._displayNameFor(speaker);

    if (speaker) {
      rootElement.classList.add(`chat-bubble-tandem`);
      rootElement.classList.add(`chat-bubble-tandem-${speaker}`);
      rootElement.dataset.tandemSpeaker = speaker;
      rootElement.dataset.tandemSpeakerLabel = label || "";
    } else {
      delete rootElement.dataset.tandemSpeaker;
      delete rootElement.dataset.tandemSpeakerLabel;
    }

    if (lane) {
      rootElement.classList.add(`chat-bubble-tandem-lane-${lane}`);
      rootElement.dataset.tandemLane = lane;
    } else {
      delete rootElement.dataset.tandemLane;
    }
  }
}

export default TandemBubbleUiSystem;
